import { InlineModalFrame } from 'views/Agent/form-frame';
import { FormOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Modal } from 'antd';
import { ButtonProps } from 'antd/es/button';
import { getStoreCount, StoreScopesModal } from 'components/StoreScopes';
import { permissionNameIdMap } from 'hooks/business/useNewPermisson';
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { createPhraseGroupV2 } from 'services/api/phrase';
import {
  storeScopes2applyScope,
  useApplyScope2storeScopes,
} from 'views/Aide/Basis/Remind/TaskForm/share';

const { useForm } = Form;

export const NAME_MAX_LENGTH = 40;

export interface GroupAddProps {
  type?: ButtonProps['type'];
  groupType: Phrase.Group['groupType'];
  channel: General.PlatformUpperCaseTunnel;
  onSaved?: (id?: string) => void;
  agentCreate?: { name?: string; presentation?: 'sidebar' | 'business'; onClose(): void };
}

export const GroupAdd = (props: GroupAddProps) => {
  const { type, groupType, channel, onSaved } = props;
  const [visible, setVisible] = useState(Boolean(props.agentCreate));
  const [loading, setLoading] = useState(false);
  const [form] = useForm();
  const { t: trans } = useTranslate();
  const Frame = props.agentCreate?.presentation === 'sidebar' ? InlineModalFrame : Modal;

  return (
    <>
      {!props.agentCreate && (
        <Button type={type} icon={<PlusOutlined />} onClick={() => setVisible(true)}>
          {trans('phrase.添加分组')}
        </Button>
      )}
      <Frame
        okText={trans('phrase.保存')}
        cancelText={trans('translation.取消')}
        width={400}
        title={trans('phrase.添加分组')}
        visible={visible}
        maskClosable={false}
        onCancel={() => {
          setVisible(false);
          props.agentCreate?.onClose();
          setLoading(false);
          form?.resetFields();
        }}
        onOk={() => {
          form?.submit();
        }}
        okButtonProps={{
          loading,
        }}
      >
        <Form
          initialValues={{
            name: props.agentCreate?.name || '',
            applyScope: {
              scope: 'STORES',
              storesScope: {
                tunnelTypeRegion: {},
              },
              tunnelTypeScope: {
                tunnelTypes: [channel],
              },
              tunnelTypeRegionScope: {
                tunnelTypeRegion: {},
              },
            } as Phrase.ApplyScocpe,
          }}
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            setLoading(true);
            try {
              const data = await createPhraseGroupV2({
                groupType,
                channel,
                name: values.name,
                applyScope: values.applyScope,
              });
              message.success(
                trans('api.common.success.messages', { action: trans('api.common.action.save') }),
              );
              setVisible(false);
              form?.resetFields();
              onSaved?.(data && data.id);
            } catch (err) {
              message.error(
                trans('api.common.error.messages', { action: trans('api.common.action.save') }),
              );
            } finally {
              setLoading(false);
            }
          }}
        >
          <Form.Item
            name="name"
            label={trans('phrase.分组名称')}
            required
            rules={[{ required: true, message: trans('phrase.请输入分组名称') }]}
          >
            <Input
              placeholder={trans('phrase.请输入分组名称')}
              maxLength={NAME_MAX_LENGTH}
              showCount
            />
          </Form.Item>
          {groupType === 'TEAM' && <ApplyScope platform={channel} />}
        </Form>
      </Frame>
    </>
  );
};

function ApplyScope({ platform }: { platform: General.PlatformUpperCaseTunnel }) {
  const { t } = useTranslate();

  const [visible, setVisible] = useState(false);

  const { applyScope2storeScopes } = useApplyScope2storeScopes({
    platform,
    permId: permissionNameIdMap['快捷回复-团队模版'],
  });

  return (
    <Form.Item noStyle dependencies={['applyScope']}>
      {({ getFieldValue, setFieldsValue }) => {
        const applyScope = getFieldValue('applyScope');

        const scopes =
          !applyScope || applyScope.scope === 'ALL' ? [] : applyScope2storeScopes(applyScope);

        const modal = (
          <StoreScopesModal
            supportWa={false}
            showFooterTips={false}
            platform={platform}
            permId={permissionNameIdMap['快捷回复-团队模版']}
            visible={visible}
            value={scopes}
            onCancel={() => setVisible(false)}
            onChange={(storeScopes) =>
              setFieldsValue({
                applyScope: storeScopes2applyScope(storeScopes),
              })
            }
          />
        );

        return (
          <>
            {modal}
            <Form.Item
              required
              name="applyScope"
              label={t('aide.应用店铺')}
              rules={[
                {
                  validator: (_, applyScope) => {
                    if (!applyScope) {
                      return Promise.reject(new Error(t('aide.请选择应用店铺')));
                    }

                    if (applyScope.scope === 'ALL') {
                      return Promise.resolve();
                    }

                    const storeCount = getStoreCount(applyScope2storeScopes(applyScope));

                    return storeCount
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('aide.请选择应用店铺')));
                  },
                },
              ]}
            >
              <div>
                {applyScope && applyScope.scope === 'ALL'
                  ? t('department.全部店铺')
                  : t('department.已选x店铺', { x: getStoreCount(scopes) })}
                <Button type="link" icon={<FormOutlined />} onClick={() => setVisible(true)}>
                  {t('marketing.编辑')}
                </Button>
              </div>
            </Form.Item>
          </>
        );
      }}
    </Form.Item>
  );
}
